import type { VoyageReportKind } from './schemas.js';

/**
 * Okunur rapor numarası (ADR 0057 §6): saklanmaz, türetilir. `<sefer no>-<tür öneki>-<nn>`;
 * `nn` seferdeki aynı türün silinmemiş raporları arasında kimlik (UUIDv7, oluşturma) sırasıdır.
 * Kalıcı numara (rapor başlığı) M15b'dedir.
 */
export const REPORT_PREFIXES: Readonly<Record<VoyageReportKind, string>> = {
  departure: 'DEP',
  cosp: 'COSP',
  noon: 'NOON',
  eosp: 'EOSP',
  arrival: 'ARR',
  port_daily: 'PRTD',
  anchorage_start: 'ANC',
  anchorage_daily: 'ANCD',
  anchorage_end: 'ANCU',
  waiting_start: 'WAIT',
  waiting_end: 'WAITE',
  drift_start: 'DRF',
  drift_daily: 'DRFD',
  drift_end: 'DRFE',
  strait_entry: 'STRI',
  strait_exit: 'STRO',
  canal_entry: 'CNLI',
  canal_exit: 'CNLO',
  cargo_start: 'CRGS',
  cargo_complete: 'CRGC',
  on_hire: 'ONH',
  off_hire: 'OFFH',
};

export function voyageReportNumber(
  voyageNo: string,
  report: { id: string; kind: VoyageReportKind },
  reports: readonly { id: string; kind: VoyageReportKind }[],
): string {
  const same = reports
    .filter((r) => r.kind === report.kind)
    .map((r) => r.id)
    .sort();
  const index = same.indexOf(report.id);
  const ordinal = index < 0 ? same.length + 1 : index + 1;
  return `${voyageNo}-${REPORT_PREFIXES[report.kind]}-${String(ordinal).padStart(2, '0')}`;
}

/**
 * Kalıcı rapor numarası (ADR 0058 §5): `<önek>-<YYYY>-<NNNN>`; yıl raporun oluşturulduğu yıl
 * (UTC), sıra gemide aynı tür ve yıldaki raporlar arasında kimlik (UUIDv7, oluşturma) sırası.
 * Silinmiş taslaklar da sayılır: numara bir kez verilir, yeniden kullanılmaz. Saklanmaz; gemi ve
 * ofis aynı satırlardan aynı numarayı türetir (satırlar sırayla senkronlanır).
 * @param vesselReports Geminin aynı türden bütün raporları (silinmişler dahil).
 */
export function permanentReportNumber(
  report: { id: string; kind: VoyageReportKind; createdAt: string },
  vesselReports: readonly { id: string; kind: VoyageReportKind; createdAt: string }[],
): string {
  const year = new Date(report.createdAt).getUTCFullYear();
  const same = vesselReports
    .filter((r) => r.kind === report.kind && new Date(r.createdAt).getUTCFullYear() === year)
    .map((r) => r.id)
    .sort();
  const index = same.indexOf(report.id);
  const ordinal = index < 0 ? same.length + 1 : index + 1;
  return `${REPORT_PREFIXES[report.kind]}-${year}-${String(ordinal).padStart(4, '0')}`;
}

/** "Amended" (tel kafes): yeniden açılıp tekrar gönderilmiş rapor; türetilir (ADR 0058 §5). */
export function reportAmended(report: { status: string; revision: number }): boolean {
  return report.status === 'submitted' && report.revision > 1;
}
